import React, { FC, useCallback, useEffect, useReducer } from 'react';

import { NativeResources } from '@ariksa/abac/api';
import { Box, Center, HStack } from '@chakra-ui/react';
import { find, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { QueryStatus } from 'services/types';
import { v4 as uuidv4 } from 'uuid';

import { AuditTrailApiService, authTokenHeader } from 'api/services';
import { Card, FormActionErrorMessage, getIcon } from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { Form, FormAction } from 'components/DataEntry';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { ObjectStorageIcon, SuddenActivityIcon } from 'components/Icons';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectIncidentResponse } from 'containers/Visibility/IncidentResponse/selectors';
import { actions } from 'containers/Visibility/IncidentResponse/slice';

import {
  flattenSelectedPaths,
  flattenPaths,
  getSelectedFolderPaths,
  SelectOption,
} from '../utils';

import {
  ActivityGroupFormResource,
  ActivityResourceRow,
} from './ActivityGroupFormResource';

interface Props {
  action?: FormAction;
}

interface FormState {
  name: string;
  description: string;
  resources: ActivityResourceRow[];
  resourceOptions: SelectOption[];
  initializedFor?: string;
  isInvalid: boolean;
}

export const ActivityGroupForm: FC<Props> = ({ action = 'Add' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { group_id } = useParams<{ group_id: string }>();
  const { accountId: boundaryAccountId, environmentId } = useAccessBoundary();
  const { accountMapping } = useCloudAccountId();
  const { setLearnMore } = useKnowledgeBase();
  const {
    activityGroup,
    activityGroupAction,
    activityGroupBuckets,
    activityGroupBucketsByArn,
    activityObjectPaths,
    objectPathsByBucket,
    activityResources,
  } = useSelector(selectIncidentResponse);
  const isEdit = action === 'Update';

  const [state, updateState] = useReducer(
    (
      previous: FormState,
      next: Partial<FormState> | ((previous: FormState) => FormState),
    ) =>
      typeof next === 'function' ? next(previous) : { ...previous, ...next },
    {
      name: '',
      description: '',
      resources: [],
      resourceOptions: [],
      isInvalid: false,
    },
  );
  const { resourceOptions } = state;

  useEffect(() => {
    return () => {
      dispatch(actions.resetActivityGroup());
    };
  }, [dispatch]);

  // Use the same inventory resource request as the Activity Graph drawer.
  useEffect(() => {
    if (!environmentId && !boundaryAccountId) return;

    dispatch(
      actions.getActivityResources({
        q: {
          nativeResource: NativeResources.SimpleStorageService,
          environmentId,
          accountId: Array.isArray(boundaryAccountId)
            ? boundaryAccountId
            : [boundaryAccountId],
        },
      }),
    );
  }, [boundaryAccountId, dispatch, environmentId]);

  useEffect(() => {
    updateState({
      resourceOptions: map(activityResources.data, o => ({
        label: o.name ?? o.resource_id,
        value: o.resource_id,
        icon: <Center color={'orange.300'}>{getIcon(o.resource_type)}</Center>,
        accountId: accountMapping?.[o.account]?.cloud_account_id ?? o.account,
        bucketName: o.name ?? o.resource_id,
      })),
    });
  }, [activityResources.data, accountMapping]);
  console.log(activityGroup.data);

  useEffect(() => {
    if (isEdit) {
      updateState({
        name: activityGroup.data?.name as string,
        description: activityGroup.data?.description as string,
      });
    }
  }, [isEdit, activityGroup.data]);

  const requestObjectPaths = useCallback(
    (resource?: SelectOption | null, force = false) => {
      const bucketName = resource?.bucketName;
      if (!bucketName) return;

      const objectPathsPayload = {
        body: {
          buckets: [
            {
              bucket_name: bucketName,
              account_id: resource?.accountId,
            },
          ],
        },
      };

      dispatch(
        actions.getActivityObjectPaths({
          q: objectPathsPayload,
          status: QueryStatus.pending,
        }),
      );

      AuditTrailApiService.AuditTrail.auditTrailServiceCloudEventsBucketPathSuggestions(
        objectPathsPayload,
        authTokenHeader(),
      )
        .then(response => {
          dispatch(
            actions.getActivityObjectPaths({
              q: objectPathsPayload,
              status: QueryStatus.fulfilled,
              data: response.data,
            }),
          );
        })
        .catch(() => {
          dispatch(
            actions.getActivityObjectPaths({
              q: objectPathsPayload,
              status: QueryStatus.failed,
            }),
          );
        });
    },
    [dispatch],
  );

  // Select the first available resource when creating a new activity group.
  // Edit forms load their saved resources separately and must not overwrite them.
  useEffect(() => {
    if (isEdit || state.initializedFor || !resourceOptions.length) return;

    requestObjectPaths(resourceOptions[0]);
    updateState({
      resources: [
        {
          id: uuidv4(),
          resource: resourceOptions[0],
          folders: [],
        },
      ],
      initializedFor: 'new',
    });
  }, [
    isEdit,
    requestObjectPaths,
    resourceOptions,
    state.initializedFor,
    accountMapping,
  ]);

  // Restore the existing group only after all three requests have completed.
  useEffect(() => {
    if (
      !isEdit ||
      !group_id ||
      state.initializedFor === group_id ||
      !activityGroup.isSuccess ||
      !activityGroupBuckets.isSuccess ||
      !activityResources.isSuccess
    )
      return;

    const resources = Object.values(activityGroupBucketsByArn).map(bucket => {
      const bucketName = String(bucket.bucket_name ?? bucket.bucket_arn ?? '');
      const bucketArn = String(bucket.bucket_arn ?? '');
      const accountId = String(bucket.account_id ?? '');
      const resource = find(
        resourceOptions,
        option =>
          option.value === bucketArn || option.bucketName === bucketName,
      );
      const restoredResource = resource
        ? {
            ...resource,
            accountId: accountId || resource.accountId,
            bucketName,
          }
        : {
            label: bucketName || '-',
            value: bucketArn || bucketName,
            icon: <ObjectStorageIcon />,
            accountId,
            bucketName,
          };

      return {
        id: uuidv4(),
        resource: restoredResource,
        // Restore nested folders as selected options as well as top-level paths.
        folders: flattenSelectedPaths(bucket.paths ?? []),
      };
    });
    resources.forEach(row => {
      if (row.resource) requestObjectPaths(row.resource, true);
    });
    updateState({
      name: activityGroup.data.name ?? '',
      description: activityGroup.data.description ?? '',
      resources,
      initializedFor: group_id,
    });
  }, [
    activityGroup.data,
    activityGroup.isSuccess,
    activityGroupBucketsByArn,
    activityGroupBuckets.isSuccess,
    activityResources.isSuccess,
    group_id,
    isEdit,
    requestObjectPaths,
    resourceOptions,
    state.initializedFor,
  ]);

  const updateRow = (rowId: string, update: Partial<ActivityResourceRow>) =>
    updateState(previous => ({
      ...previous,
      resources: previous.resources.map(row =>
        row.id === rowId ? { ...row, ...update } : row,
      ),
      isInvalid: false,
    }));

  const removeRow = (rowId: string) =>
    updateState(previous => ({
      ...previous,
      resources: previous.resources.filter(row => row.id !== rowId),
      isInvalid: false,
    }));

  const addResource = () => {
    const selectedResources = new Set(
      state.resources.map(row => row.resource?.value).filter(Boolean),
    );
    const resource = resourceOptions.find(
      option => !selectedResources.has(option.value),
    );

    if (!resource) return;

    requestObjectPaths(resource);
    updateState(previous => ({
      ...previous,
      resources: [
        ...previous.resources,
        { id: uuidv4(), resource, folders: [] },
      ],
      isInvalid: false,
    }));
  };

  const onSubmit = () => {
    const buckets = state.resources
      .filter(row => row.resource?.value)
      .map(row => ({
        account_id: row.resource?.accountId,
        bucket_arn: row.resource?.value,
        relative_paths: getSelectedFolderPaths(
          row.folders,
          flattenPaths(
            objectPathsByBucket[row.resource?.bucketName ?? ''] ?? [],
          ),
        ),
      }));
    const payload = {
      name: state.name,
      description: state.description,
      buckets,
    };
    const onSuccess = () => {
      updateState({ isInvalid: false });
      navigate('/visibility/activity?tab=Data');
    };

    if (isEdit && group_id) {
      dispatch(
        actions.updateActivityGroup({
          q: { activityGroupId: group_id, body: payload },
          onSuccess,
          onError: () => updateState({ isInvalid: true }),
        }),
      );
    } else {
      dispatch(
        actions.createActivityGroup({
          q: { body: payload },
          onSuccess,
          onError: () => updateState({ isInvalid: true }),
        }),
      );
    }
  };

  const isLoading =
    activityResources.isLoading ||
    (isEdit && (activityGroup.isLoading || activityGroupBuckets.isLoading));
  const hasResource = state.resources.some(row => row.resource?.value);

  return (
    <Card styles={{ cardContent: { pl: 32, ml: 2 } }}>
      <Form
        title={
          <HStack justify="space-between" w="full" flex={1} minW={0}>
            <Box minW={0}>{isEdit ? 'Update' : 'Create'} Activity Group</Box>
            <LearnMoreButton
              ml="auto"
              onOpen={() => setLearnMore('activity_group')}
              label="Activity Group"
            />
          </HStack>
        }
        titleIcon={<SuddenActivityIcon />}
        isLoading={isLoading}
        schema={{
          name: {
            type: 'text',
            label: 'Name',
            value: state.name,
            onChange: value => updateState({ name: value, isInvalid: false }),
            placeholder: 'Activity Group name',
            isRequired: true,
            styles: { control: { w: '50%' } },
          },
          description: {
            type: 'textArea',
            label: 'Description',
            value: state.description,
            onChange: value =>
              updateState({ description: value, isInvalid: false }),
            placeholder:
              'This description is to codify what actions or policies need to be enforced when a certain condition is detected or found in the cloud environment',
            isRequired: true,
          },
          resources: {
            type: 'custom',
            isRequired: true,
            component: () => (
              <ActivityGroupFormResource
                resources={state.resources}
                resourceOptions={resourceOptions}
                objectPathsByBucket={objectPathsByBucket}
                isObjectPathsLoading={activityObjectPaths.isLoading}
                onAddResource={addResource}
                onResourceChange={requestObjectPaths}
                onUpdateRow={updateRow}
                onRemoveRow={removeRow}
              />
            ),
          },
          ...(state.isInvalid
            ? {
                validatedMessage: {
                  type: 'custom',
                  component: () => (
                    <FormActionErrorMessage
                      description={activityGroupAction.error?.description}
                    />
                  ),
                },
              }
            : {}),
        }}
        buttonOptions={{
          submit: {
            name: 'OK',
            isLoading: activityGroupAction.isLoading,
            isDisabled: !state.name || !state.description || !hasResource,
            tooltip: !state.name
              ? 'Please provide name'
              : !state.description
              ? 'Please provide description'
              : !hasResource
              ? 'Please add one or more resources'
              : undefined,
          },
          reset: {
            name: 'CANCEL',
            onClick: () => navigate('/visibility/activity?tab=Data'),
            isVisible: true,
          },
        }}
        handleSubmit={onSubmit}
        styles={{
          form: {
            formWidth: '3xl',
            title: { labelStyles: { w: 'full' } },
          },
        }}
      />
      <LearnMore />
    </Card>
  );
};
